import {
  Bot,
  FileText,
  KeyRound,
  Plug,
  Play,
  Server,
  Settings2,
  ShieldCheck,
  Sparkles,
  Users,
} from 'lucide-react';

import type { Role } from '@/stores/session';
import type { Guide } from '../types';

/** Issuing keys + operating the hosted server is Founder / Super Admin work. */
const ADMIN_ROLES: Role[] = ['super_admin', 'founder'];

export const AI_GUIDES: Guide[] = [
  {
    slug: 'ai-overview',
    title: 'Claude & MCP — overview',
    module: 'ai',
    summary: 'Use Claude (or any AI assistant) to work with Dev13 over the MCP server.',
    icon: Sparkles,
    body: `**MCP** (Model Context Protocol) lets an AI assistant like **Claude** act on
your Dev13 data through a safe, audited bridge — read and write **Projects,
Pages/Wiki, CRM and HRM**, scoped to your organization.

### What you can do
- Ask in plain language: *"list my in-progress work items"*, *"create a CRM lead
  for Acme"*, *"add a page to the Onboarding wiki"*, *"who's blocking GLOBEX-2?"*.
- It uses the same permissions you have — nothing it does bypasses RBAC or
  field-masking, and every write is recorded in the audit log.

### How it fits together
\`\`\`
You ↔ Claude (Code or Desktop)  --MCP-->  Dev13 MCP server  --API key-->  Dev13
\`\`\`
You connect once with your **personal API key**; from then on Claude can call the
Dev13 tools on your behalf.

### Get started
1. Get a key — see **"Get your API key"**.
2. Connect Claude — **"Connect Claude Code"** or **"Connect Claude Desktop"**.`,
  },
  {
    slug: 'ai-get-key',
    title: 'Get your API key',
    module: 'ai',
    summary: 'Ask a workspace admin for a personal token to connect your assistant.',
    icon: KeyRound,
    body: `To connect Claude you need a **personal API key** that represents you — it
acts as **you**, with exactly your own roles and access. Nothing it does can
exceed what you can already do in Dev13.

### How to get one
**Ask a workspace admin** to create a personal token for you. They'll send it over
securely.

### Keep it safe
- The key is shown **once** — save it in a password manager.
- Treat it like a password: never paste it in chat or commit it to code.
- If it ever leaks, ask an admin to **rotate or revoke** it.`,
  },
  {
    slug: 'ai-connect-claude-code',
    title: 'Connect Claude Code',
    module: 'ai',
    summary: 'Add the Dev13 MCP server to the Claude Code CLI.',
    icon: Plug,
    body: `**Hosted (recommended)** — nothing to install; point at the team server with
your key:

\`\`\`
claude mcp add --transport http dev13 https://mcp.<your-domain>/mcp \\
  --header "X-Api-Key: <your personal token>"
\`\`\`

Add \`-s user\` to make it available in every project. Then restart Claude Code and
run \`claude mcp list\` — it should show \`dev13 … ✔ Connected\`. Try *"run the whoami
tool"* or *"list Dev13 projects"*.

Replace \`mcp.<your-domain>\` with your workspace's MCP address (ask an admin) and
\`<your personal token>\` with your key.`,
  },
  {
    slug: 'ai-connect-claude-desktop',
    title: 'Connect Claude Desktop',
    module: 'ai',
    summary: 'Add Dev13 to Claude Desktop as a remote MCP server.',
    icon: Plug,
    body: `In Claude Desktop's config (\`claude_desktop_config.json\`), add a remote MCP
server:

\`\`\`
{
  "mcpServers": {
    "dev13": {
      "type": "http",
      "url": "https://mcp.<your-domain>/mcp",
      "headers": { "X-Api-Key": "<your personal token>" }
    }
  }
}
\`\`\`

Fully quit and reopen Claude Desktop. The Dev13 tools appear under the tools/
connectors menu. If it doesn't show, check **Settings → Developer** for status.`,
  },
  {
    slug: 'ai-what-it-can-do',
    title: 'What Claude can do here (tools, prompts, resources)',
    module: 'ai',
    summary: 'The Dev13 tools, ready-made prompts, and page resources.',
    icon: Bot,
    body: `Once connected, Claude has Dev13 **tools**, **prompts** and **resources**.

### Tools (read + write)
- **Projects** — projects, work items, states, cycles, modules (create + update
  status), relations (blocks/blocked-by), worklogs, attachments, work-item comments.
  **Log time** against a work item as hours ("1.5", "1h30min", "1:30", "45m") or
  \`minutes\`.
- **Pages / Wiki** — list/read/create/update/delete pages and page subtrees. Updates
  also **publish / unpublish** (\`published\`) and **archive / restore**
  (\`is_archived\`); a new project page can be created published (\`publish\`). Keys a
  page doesn't have are refused by name, never silently dropped. \`delete_project_page\`
  / \`delete_wiki_page\` refuse a page with sub-pages unless \`cascade\` (then only if
  you can reach every sub-page). Long lists come in pages (\`next_page\`), and
  subtrees leave bodies out unless asked.
- **CRM** — companies, contacts, leads, deals, proposals, activities, and the **bid
  ledger**: \`list_bids\`, \`get_bid\`, \`list_bid_rollup\` (read); \`upsert_bid\` and
  \`convert_bid_to_lead\` (write).

### \`upsert_bid\` — the Upwork sync's write path
Creates or refreshes one bid keyed on **channel + job id + proposal id**, so the
daily sync can call it once per proposal, every run, without duplicates. Pass
\`owner_id\` and \`bidder_profile\` from the **connected Upwork account** the
proposal came from — each account syncs only its own proposals, and that
ownership flows into the Lead and Deal (commission). \`data\` carries the bid fields;
\`funnel\` carries the job's funnel as read right now (stored as the at-submit
snapshot the first time, as the latest snapshot every time). Fields a human
edited in the UI are never overwritten. A \`room_id\` auto-converts the bid to a
Lead; \`job_hires > 0\` without a \`contract_id\` auto-closes it. The bot key needs
the writer tier (it holds *add* on bids only — it can upsert and convert, never edit, archive or restore — see *Bids* in the CRM guides).
- **HRM** — departments, employees, time-off, skills (salary stays masked unless
  your role can see it).

### Prompts (task templates)
\`standup\`, \`triage\`, \`sprint_summary\` — pick one to have Claude run a common task.

### Resources (read-only context)
\`dev13://wiki/{id}\` and \`dev13://project-page/{id}\` pull a page's content straight
into the conversation.

Everything respects your roles and is audited — Claude can't see or do more than
you can.`,
  },
  {
    slug: 'ai-safe-use',
    title: 'Safe, effective use',
    module: 'ai',
    summary: 'Least privilege, masking, and good habits when working through AI.',
    icon: ShieldCheck,
    body: `The bridge is safe by design — keep it that way:

- **Least privilege.** Use a key with only the roles you need. A read-only key is
  great for "ask questions"; reserve write/admin keys for when you actually edit.
- **Masking holds.** Salary / tax / payslip fields never reach the assistant unless
  your role can see them — same as the app.
- **Writes are real + audited.** When Claude creates or edits, it's a real change
  credited to you. Review before confirming bulk actions.
- **Guard the key.** It's like your password — never share or commit it, and ask
  an admin to rotate it if it leaks.`,
  },
  {
    slug: 'ai-admin-keys',
    title: 'Issuing & managing keys (admins)',
    module: 'ai',
    summary: 'Create bots and personal tokens, set roles, rotate and revoke.',
    icon: KeyRound,
    roles: ADMIN_ROLES,
    body: `From **Settings → API Keys & Bots** (Founder / Super Admin):

### Create
- **Bot** — name + roles → a standalone service account. Grant the least it needs;
  a Founder bot can do everything in the org.
- **Personal token** — pick an existing person → the key acts as **them** with their
  own roles and project access. Best for connecting an individual's Claude.

The raw key is shown **once** (on create and on rotate). Share it via a secrets
manager, never chat.

### Manage
- **Rotate** replaces a key (the old one stops working immediately).
- **Revoke** disables it — do this the moment a key leaks or a person leaves.
- Prefer **one key per person/use-case** so audit and revocation are clean.`,
  },
  {
    slug: 'ai-hosted-deploy',
    title: 'Hosting the MCP server (admins)',
    module: 'ai',
    summary: 'How the team-wide MCP server is deployed and operated.',
    icon: Server,
    roles: ADMIN_ROLES,
    body: `The MCP server runs as part of the platform so teammates only need a URL.

### How it's wired
- An isolated **\`mcp\`** service in Docker Compose (own image), reaching the API on
  the internal network, fronted by **Caddy** at \`mcp.<domain>\` (auto-TLS).
- **No key on the server** — each request carries the caller's own \`X-Api-Key\`, so
  one server serves the whole team, each acting as their own bot.
- Built and shipped by **CI** like the app image (build → push → deploy pulls it).

### Operating it
- Bring it up with the rest of the stack (\`docker compose -f production.yml up -d\`)
  and add a DNS record for \`mcp.<domain>\`.
- It's stateless apart from per-request keys; restart freely. Revoke a leaked key
  from Settings — the server holds no credentials itself.

Full details live in the repo's \`integrations/mcp/README.md\`.`,
  },
  {
    slug: 'ai-team-overview',
    title: 'The AI Team',
    module: 'ai',
    summary: 'A directory of specialist agents that work from your knowledge.',
    icon: Users,
    body: `**AI Team** is your organization's roster of specialist agents — a marketing
strategist, a proposal writer, a market-intelligence radar, and more. Each is a
focused **playbook** that runs inside Claude and works from your organization's
knowledge.

### Directory vs Results
The AI Team has two sub-pages in the sidebar:
- **Directory** — browse installed agents, grouped by direction (Strategy,
  Marketing, Sales…). Open one to see what it does and how to run it.
- **Results** — the outputs your team has produced across every agent (see
  **Agent results**).

### How agents work
An agent isn't a separate app — it's a definition Claude adopts. You connect
Claude to Dev13 once (see **Connect Claude Code / Desktop**), then ask Claude to
use an agent by name. It loads that agent's playbook, tools, and your knowledge,
and gets to work. See **Running an agent in Claude**.

### Public vs private
A 🔒 lock marks a **private** agent — visible only to the people explicitly added
to it. Public agents are available to all staff. An admin manages who can see
each agent.`,
  },
  {
    slug: 'ai-run-agent',
    title: 'Running an agent in Claude',
    module: 'ai',
    summary: 'Connect once, call the agent by name, give it the task, save the output.',
    icon: Play,
    body: `Every agent's detail page opens with a **Quickstart** — the exact steps plus
copy-paste example prompts. In short:

### 1. Connect Dev13 once
Add Dev13 as an MCP connector in **Claude Code**, the **desktop app**, or via the
**API** — one-time per person (see **Connect Claude Code / Desktop**).

### 2. Call the agent by name
In a Claude chat, say e.g. *"Use the Dev13 \`north\` agent."* Claude loads that
agent's playbook, its allowed tools, and your organization's knowledge.

### 3. Give it the task
Name the action and inputs — the Quickstart lists ready-made example prompts you
can copy (e.g. *"Run a demand radar for this quarter…"*).

### 4. Save the output (optional)
Ask Claude to save the result. It's stored in **AI Team → Results**, retained even
after you paste it elsewhere.

Agents act with **your** access and knowledge — nothing they do bypasses your
permissions, and private knowledge stays private (see the Knowledge Base
**Privacy & access** guide).`,
  },
  {
    slug: 'ai-agent-results',
    title: 'Agent results',
    module: 'ai',
    summary: 'Saved outputs — yours by default, shared when you choose.',
    icon: FileText,
    body: `When an agent produces something worth keeping, it's saved as a **result** —
a titled Markdown output you can reopen, copy, download, or share.

### Where to find them
- **AI Team → Results** — every result you can see, across all agents.
- An agent's **Results** button — just that agent's outputs.

### Who sees what
- **Mine** — the results you produced.
- **Shared** — results a teammate explicitly shared.
- **All** — Founders / Super Admins can see everything.

A result is private to its author until they flip the **Share** switch. So you can
draft freely and only surface the ones worth circulating.

### Status
Results carry a state — **draft**, **final**, or **posted** — so you can tell a
work-in-progress from something already sent.`,
  },
  {
    slug: 'ai-agent-admin',
    title: 'Managing agents (admins)',
    module: 'ai',
    summary: 'Add agents to the team and set tools, knowledge, visibility and versions.',
    icon: Settings2,
    roles: ADMIN_ROLES,
    body: `From **Settings → Agents** (Founder / Super Admin) you curate the team.

### Add to team
Pick an agent from the catalog and **Add to team**. Set:
- **Status** (draft / active / paused) and **Enabled**.
- **Pinned version** — track the latest, or pin a specific version.
- **Allowed tools** — the tools the agent may use (advisory; role tiers govern the
  real ceiling). Click **✨ Recommended** to prefill sensible defaults.
- **KB tags** — restrict the agent's retrieval to knowledge tagged this way (again
  with a **✨ Recommended** prefill per direction).

### Visibility
- **Public** — every staff member can see and run it.
- **Private** — only the members you choose. Private agents show a 🔒 lock in the
  roster and on the detail page.

### Image
Upload a custom icon per agent (camera overlay on the header tile), or reset to the
bundled starter icon.

### Finding agents
The admin list filters by **direction** and by **on team / not on team**, so you
can see what's live versus still available to add.`,
  },
];
